import {
  createBloodGlucoseForm,
  getFormById,
  getLastBloodGlucoseObservationWithReading,
  getObservationById,
  getPatientObservations,
  updateBloodGlucoseForm,
} from "@/data/requests/measurementRequests"
import type { BloodGlucoseFormApiDto } from "@/model/BloodGlucoseForm"
import { toBloodGlucoseForm } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseObservationApiDto } from "@/model/BloodGlucoseObservation"
import {
  toBloodGlucoseObservation,
  toBloodGlucoseObservationWithReading,
} from "@/model/BloodGlucoseObservation"
import type { BloodGlucoseObservationsApiDto } from "@/model/BloodGlucoseObservations"
import { toBloodGlucoseObservations } from "@/model/BloodGlucoseObservations"
import { ZonedDateTime } from "@js-joda/core"
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const observationsListQueryKey = (patientId: string) => ["observations", patientId]
const lastObservationQueryKey = (patientId: string) => ["last-observation", patientId]
const formQueryKey = (formId: string) => ["form", formId]
const observationQueryKey = (readingId: string) => ["observation", readingId]

export const usePatientObservationsQuery = ({
  patientId,
  from,
  to,
}: {
  patientId: string | undefined
  from: ZonedDateTime
  to: ZonedDateTime
}) => {
  // Instant ensures there's always a seconds component and a Z suffix.
  const fromIso = from.toInstant().toString()
  const toIso = to.toInstant().toString()
  return useQuery({
    queryKey: ["observations", patientId, fromIso, toIso],
    queryFn: patientId ? () => getPatientObservations({ patientId, fromIso, toIso }) : skipToken,
    select: toBloodGlucoseObservations,
    staleTime: 60_000,
  })
}

export const useLastBloodGlucoseObservationWithReadingQuery = ({
  patientId,
}: {
  patientId: string | undefined
}) =>
  useQuery({
    queryKey: ["last-observation", patientId],
    // The last-observation endpoint returns 204 No Content when the patient
    // has no readings yet. jsonFetch surfaces that as undefined; React Query
    // refuses undefined data, so coerce to null.
    queryFn: patientId
      ? async () => {
          const dto = await getLastBloodGlucoseObservationWithReading({ patientId })
          return dto ? toBloodGlucoseObservationWithReading(dto) : null
        }
      : skipToken,
    staleTime: 60_000,
  })

export const useObservationByIdQuery = ({ readingId }: { readingId: string | null }) => {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ["observation", readingId],
    queryFn: readingId ? () => getObservationById({ readingId }) : skipToken,
    // The contextualization deeplink can arrive while the SmartMeter→backend
    // pipeline is still propagating: tap the SMS link in <30s and the
    // observation may 404. Retry up to ~120s before giving up.
    retry: 24,
    retryDelay: 5_000,
    // If the observation is already in any cached observations list, hand it
    // back synchronously so the edit page renders without a spinner round-trip.
    initialData: () => {
      if (!readingId) return undefined
      return findObservationInListsCache(queryClient, readingId)
    },
    select: toBloodGlucoseObservation,
  })
}

export const useFormByIdQuery = ({ formId }: { formId: string | null }) =>
  useQuery({
    queryKey: ["form", formId],
    queryFn: formId ? () => getFormById({ formId }) : skipToken,
    select: toBloodGlucoseForm,
  })

export const useCreateBloodGlucoseFormMutation = ({ patientId }: { patientId: string }) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createBloodGlucoseForm,
    // Optimistic update only when contextualizing an existing reading
    // (the readingId is known up-front). For brand-new readings the readingId
    // is server-generated, so there's no cache entry to update — we rely on
    // onSettled invalidation.
    onMutate: async ({ form }) => {
      if (!form.readingId) return undefined
      const observationKey = observationQueryKey(form.readingId)
      await queryClient.cancelQueries({ queryKey: observationKey })
      const previous = queryClient.getQueryData<BloodGlucoseObservationApiDto>(observationKey)
      if (previous) {
        const optimisticForm = optimisticFormFromCreate(patientId, form)
        queryClient.setQueryData<BloodGlucoseObservationApiDto>(observationKey, {
          ...previous,
          form: optimisticForm,
        })
      }
      return () => {
        if (previous) queryClient.setQueryData(observationKey, previous)
      }
    },
    onError: (_err, _variables, rollback) => {
      if (rollback) rollback()
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: observationsListQueryKey(patientId) })
      queryClient.invalidateQueries({ queryKey: lastObservationQueryKey(patientId) })
    },
  })
}

export const useUpdateBloodGlucoseFormMutation = ({
  patientId,
  formId,
}: {
  patientId: string
  formId: string
}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateBloodGlucoseForm,
    onMutate: async ({ update }) => {
      const rollbacks: Array<() => void> = []

      const formKey = formQueryKey(formId)
      await queryClient.cancelQueries({ queryKey: formKey })
      const previousForm = queryClient.getQueryData<BloodGlucoseFormApiDto>(formKey)
      if (previousForm) {
        queryClient.setQueryData<BloodGlucoseFormApiDto>(formKey, { ...previousForm, ...update })
        rollbacks.push(() => queryClient.setQueryData(formKey, previousForm))
      }

      return () => rollbacks.forEach((rollback) => rollback())
    },
    onError: (_err, _variables, rollback) => {
      if (rollback) rollback()
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: formQueryKey(formId) })
      queryClient.invalidateQueries({ queryKey: ["observation"] })
      queryClient.invalidateQueries({ queryKey: observationsListQueryKey(patientId) })
      queryClient.invalidateQueries({ queryKey: lastObservationQueryKey(patientId) })
    },
  })
}

// Read every cached `["observations", patientId, ...]` list and return the
// first entry whose reading.id matches. Used as initialData for the
// single-observation query so the edit page can render without an extra fetch.
const findObservationInListsCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  readingId: string,
): BloodGlucoseObservationApiDto | undefined => {
  const lists = queryClient.getQueriesData<BloodGlucoseObservationsApiDto>({
    queryKey: ["observations"],
  })
  for (const [, payload] of lists) {
    const match = payload?.observations.find((observation) => observation.reading?.id === readingId)
    if (match) return match
  }
  return undefined
}

const optimisticFormFromCreate = (
  patientId: string,
  form: Parameters<typeof createBloodGlucoseForm>[0]["form"],
): BloodGlucoseFormApiDto => ({
  id: `optimistic-${form.readingId ?? ""}-${crypto.randomUUID()}`,
  patientId,
  context: form.context,
  dateTime: form.dateTime ?? ZonedDateTime.now().toOffsetDateTime().toString(),
  carbsInGrams: form.carbsInGrams,
  proteinInGrams: form.proteinInGrams,
  mealContents: form.mealContents,
  medications: form.medications,
  notes: form.notes,
  reasons: form.reasons,
  readingId: form.readingId,
  editable: true,
})
